// =============================================================================
// AVAILABILITY SCOPE BADGE - Uses centralized styles (CR043)
// =============================================================================

import { MapPin, Globe, Store, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AvailabilityScope } from "@/lib/types/deals";
import { badgeStyles, getBadgeSizeClasses } from "@/lib/deals";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AvailabilityScopeBadgeProps {
  scope: AvailabilityScope;
  notes?: string | null;
  showUnknown?: boolean;
  size?: "sm" | "md";
}

const scopeConfig: Record<AvailabilityScope, {
  label: string;
  icon: typeof MapPin;
  className: string;
}> = {
  all_locations: {
    label: "All locations",
    icon: Store,
    className: "bg-green-100 text-green-800",
  },
  participating_locations: {
    label: "Participating locations",
    icon: MapPin,
    className: "bg-amber-100 text-amber-800",
  },
  online_only: {
    label: "Online only",
    icon: Globe,
    className: "bg-blue-100 text-blue-800",
  },
  unknown: {
    label: "Availability unknown",
    icon: HelpCircle,
    className: "bg-muted text-muted-foreground",
  },
};

export function AvailabilityScopeBadge({ 
  scope, 
  notes,
  showUnknown = false,
  size = "sm",
}: AvailabilityScopeBadgeProps) {
  // Hide unknown scope unless explicitly requested (for admin view)
  if (scope === "unknown" && !showUnknown) {
    return null;
  }

  const config = scopeConfig[scope] || scopeConfig.unknown;
  const Icon = config.icon;
  const sizeClasses = getBadgeSizeClasses(size);

  const badge = (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium",
        config.className,
        sizeClasses.container
      )}
    >
      <Icon className={sizeClasses.icon} />
      {config.label}
    </span>
  );

  // If notes exist, wrap in tooltip
  if (notes) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs text-sm">{notes}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
}

// Helper to get display label without rendering component
export function getAvailabilityScopeLabel(scope: AvailabilityScope): string {
  return scopeConfig[scope]?.label || "Unknown";
}
